import { Injectable } from '@angular/core';
import { DbService } from '../core/db/db.service';
import { BehaviorSubject } from 'rxjs';
import { LoggerService } from '../core/logger/logger.service';
import { UtilsService } from '../core/utils/utils.service';
import { AuthService, UserIdleStatus } from '../auth/auth.service';
import { DpNotification, GroupedDpNotifications } from '../core/shared-models/notification.model';
import { DocumentClient } from 'aws-sdk/lib/dynamodb/document_client';
import QueryInput = DocumentClient.QueryInput;
import { environment } from '../../environments/environment';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { NotificationService } from '../core/notification/notification.service';
import { SettingsService } from '../settings/settings.service';
import { DpStatusMessage } from '../core/shared-models/mp-interfaces.interface';

@Injectable({
    providedIn: 'root',
})
export class DashboardService {
    private statusMessage: DpStatusMessage = {
        enabled: false,
        message: '',
        title: '',
        type: 'warning',
        warningSoftwareMinVersion: '',
        currentSoftwareVersion: '',
    }; // status message from panel

    public statusMessage$ = new BehaviorSubject<DpStatusMessage>(this.statusMessage); // subscribeable subject to get current status message

    private groupedNotifications: Array<GroupedDpNotifications> = [];
    public groupedNotificationsList$ = new BehaviorSubject<Array<GroupedDpNotifications>>(this.groupedNotifications);

    public helpNewsFeedUnreadItemsCount$ = new BehaviorSubject<number>(0); // used to display a badge with the number of unread news items (comes from product fruits)

    constructor(
        private db: DbService,
        private authSrv: AuthService,
        private utils: UtilsService,
        private logger: LoggerService,
        private notify: NotificationService,
        private httpClient: HttpClient,
        private settingsSrv: SettingsService
    ) {
        // get status message and notifications for the first time
        setTimeout(() => {
            this.getStatusMessageFromDB();
            this.buildNotificationsList();
            this.checkUnreadHelpNewsItems();
        }, 5000);

        // refresh status message and notifications when the user becomes active
        authSrv.userIdleStatusChanged.subscribe((idleStatus) => {
            if (idleStatus == UserIdleStatus.ACTIVE) {
                this.getStatusMessageFromDB();
                this.buildNotificationsList();
            }
        });

        /*try {
            (window as any).productFruits.api.announcementsV2.listen((event) => {
                console.warn('newsfeed listen', event);
            });
        } catch (newsFeedCountErr) {
            this.logger.error('[DASHBOARD SRV] could not get product fruits newsfeed item count', newsFeedCountErr);
        }*/
    }

    /**
     * Checks product fruits service for unread news items, to display a badge
     */
    checkUnreadHelpNewsItems() {
        if (!this.utils.isOnline || !this.authSrv.isLoggedIn) {
            return;
        }
        try {
            (window as any).productFruits.api.announcementsV2.listen('newsfeed-unread-count-changed', (data) => {
                this.helpNewsFeedUnreadItemsCount$.next(data.count);
            });
        } catch (newsFeedCountErr) {
            this.logger.error('[DASHBOARD SRV] could not get product fruits newsfeed item count', newsFeedCountErr);
        }
    }

    /**
     * Builds the notifications list by getting data from db and emitting the corresponding subjects
     */
    async buildNotificationsList() {
        if (!this.utils.isOnline || !this.authSrv.isLoggedIn) {
            return;
        }
        if (!this.settingsSrv.getUserRights().canSeeCompanyWideNotifications) {
            return;
        }

        this.groupedNotifications = [];

        // query relevant notifications from dynamodb public table
        this.logger.log('[DASHBOARD SERVICE] loading notifications from dynamodb now');
        let currentTimestamp = Date.now();

        let queryComplete = false; // we will send queries until we have all items (pagination)
        let lastEvaluatedKey = null; // we get this from dynamodb if the result doesnt include all items (pagination)
        while (queryComplete === false) {
            try {
                let params: QueryInput = {
                    TableName: 'dp_notifications',
                    IndexName: 'notificationFor-notificationDate-index',
                    KeyConditionExpression: 'notificationFor = :notificationForVal AND notificationDate <= :notificationDateVal',
                    ExpressionAttributeValues: {
                        ':notificationForVal': this.authSrv.authInfo.companyData.pk,
                        ':notificationDateVal': currentTimestamp,
                    },
                };

                if (lastEvaluatedKey != null) {
                    params.ExclusiveStartKey = lastEvaluatedKey; // used to get the next results, after our previous query
                }

                let data = await this.db.dynamoDb.query(params).promise();

                if (data.LastEvaluatedKey != null) {
                    // we have not got all items, we need to query again
                    lastEvaluatedKey = data.LastEvaluatedKey;
                } else {
                    lastEvaluatedKey = null;
                    queryComplete = true;
                }

                if (data.Items != null) {
                    for (let item of data.Items) {
                        let notification = new DpNotification(item);
                        let existingNotificationProjectGroupIndex = this.groupedNotifications.findIndex((x) => {
                            return x.relatedProject === notification.relatedProject;
                        });
                        if (existingNotificationProjectGroupIndex > -1) {
                            let existingNotificationGroupIndex = this.groupedNotifications[
                                existingNotificationProjectGroupIndex
                            ].notificationGroups.findIndex((y) => {
                                return y.type === notification.type && y.name === notification.name;
                            });
                            if (existingNotificationGroupIndex > -1) {
                                this.groupedNotifications[existingNotificationProjectGroupIndex].notificationGroups[
                                    existingNotificationGroupIndex
                                ].notifications.push(notification);
                            } else {
                                this.groupedNotifications[existingNotificationProjectGroupIndex].notificationGroups.push({
                                    type: notification.type,
                                    name: notification.name,
                                    notifications: [notification],
                                });
                            }
                        } else {
                            this.groupedNotifications.push({
                                relatedProject: notification.relatedProject,
                                relatedProjectName: notification.relatedProjectName,
                                notificationGroups: [
                                    {
                                        type: notification.type,
                                        name: notification.name,
                                        notifications: [notification],
                                    },
                                ],
                            });
                        }
                    }
                }
            } catch (notificationsDbErr) {
                this.logger.error('[DASHBOARD SERVICE] could not get notifications from db', notificationsDbErr);
                this.utils.sentryCaptureException(notificationsDbErr);
                queryComplete = true;
            }
        }
        this.groupedNotificationsList$.next(this.groupedNotifications);
    }

    /**
     * Tries to delete given notifications and removes them from the notifications list
     * Only possible while online
     * @param notifications
     * @param showToasts
     */
    async dismissNotifications(notifications: Array<DpNotification>, showToasts = true) {
        if (!this.utils.isOnline) {
            if (showToasts) {
                this.notify.info('Das ist offline nicht möglich. Prüfe deine Internetverbindung');
            }
            return false;
        }

        this.logger.log('[DASHBOARD SERVICE] trying to dismiss notifications');
        let successfulCount = 0;
        try {
            let apiResponse = await this.httpClient
                .post<Array<{ pk: string; deleted: boolean }>>(
                    `${environment.AWS_API_URL}/notifications`,
                    {
                        notifications: notifications.map((x) => x.pk),
                        companyId: this.authSrv.authInfo.companyData._id,
                    },
                    {
                        headers: new HttpHeaders({
                            'Content-Type': 'application/json',
                        }),
                    }
                )
                .toPromise();
            for (let entry of apiResponse) {
                if (entry.deleted) {
                    successfulCount++;
                }
            }
            this.logger.log('[DASHBOARD SERVICE] notifications res', apiResponse);
        } catch (apiErr) {
            this.logger.error('[DASHBOARD SERVICE] could not delete notifications', apiErr);
        }

        if (successfulCount > 0) {
            if (showToasts) {
                this.notify.success('Benachrichtigung entfernt');
            }
            this.buildNotificationsList();
            return true;
        } else {
            if (showToasts) {
                this.notify.error('Benachrichtigung konnte nicht entfernt werden. Probier es bitte erneut.');
            }
            return false;
        }
    }

    /**
     * Get status message from db => for warning or error informations to the customer
     */
    getStatusMessageFromDB() {
        // check if device is online
        if (this.utils.isOnline && this.authSrv.isLoggedIn) {
            // query the item from dynamodb public table
            this.logger.log('loading maintenance status message from dynamodb now');
            this.db.dynamoDb.query(
                {
                    TableName: 'dp_public',
                    KeyConditionExpression: 'pk = :partitionVal AND sk = :sortVal',
                    ExpressionAttributeValues: {
                        ':partitionVal': 'dp-status-message',
                        ':sortVal': 'dp-status-message',
                    },
                },
                (error, data: any) => {
                    if (error) {
                        this.logger.error('[DASHBOARD SERVICE] error while getting status message from db');
                        this.utils.sentryCaptureException(error);
                        return;
                    }

                    this.statusMessage = data.Items[0].data;
                    this.statusMessage$.next(this.statusMessage);
                    this.logger.log('[DASHBOARD SERVICE] got status message from db', this.statusMessage);
                }
            );
        }
    }
}
